/**
 * Mes immersions — page dédiée au pré-recruteur pour suivre tous les
 * candidats qu'il a personnellement envoyés en immersion. Affiche les
 * status: immersion_scheduled, immersion_done, awaiting_validation,
 * validated, onboarding, hired.
 *
 * Regroupé visuellement en 3 colonnes/buckets :
 *   • À venir   (immersion_scheduled)
 *   • À évaluer (immersion_done, awaiting_validation)
 *   • Embauché·e·s (validated, onboarding, hired)
 *
 * Donne au pré-recruteur une vue suivi de bout en bout sur ses propres
 * dossiers, sans devoir cliquer sur le Calendrier ou la liste générale.
 */
import { motion } from "framer-motion";
import {
    Briefcase,
    Calendar,
    CheckCircle2,
    Clock,
    Loader2,
    MapPin,
    Search,
    Trophy,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import { useAuth } from "../lib/auth";


const FIELD_STATUSES = [
    "immersion_scheduled",
    "immersion_done",
    "awaiting_validation",
    "validated",
    "onboarding",
    "hired",
];


const STATUS_META = {
    immersion_scheduled: { label: "À venir", color: "bg-shariti-orange-soft text-shariti-orange border-shariti-orange/20", Icon: Calendar },
    immersion_done: { label: "Immersion faite", color: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
    awaiting_validation: { label: "En attente validation", color: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
    validated: { label: "Validé·e", color: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
    onboarding: { label: "Onboarding", color: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
    hired: { label: "Embauché·e", color: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: Trophy },
};


function formatDate(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        return d.toLocaleString("fr-FR", { timeZone: "Europe/Paris", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return null; }
}


function CandidateRow({ c, displayBucket }) {
    // displayBucket can override the badge for candidates whose immersion is
    // still status=immersion_scheduled but the date is past — they should
    // visually appear as "À évaluer" rather than "À venir".
    const baseMeta = STATUS_META[c.status] || STATUS_META.immersion_scheduled;
    const meta = displayBucket === "to_evaluate"
        ? { ...STATUS_META.immersion_done, label: "À évaluer" }
        : baseMeta;
    const Icon = meta.Icon;
    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-shariti-gray p-4 hover:shadow-md transition"
            data-testid={`mes-immersions-row-${c.id}`}
        >
            <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-shariti-orange-soft text-shariti-orange font-extrabold text-sm flex items-center justify-center shrink-0">
                    {c.first_name?.[0]}{c.last_name?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                    <Link to={`/candidats/${c.id}`} className="text-base font-extrabold text-shariti-black hover:text-shariti-orange transition truncate block">
                        {c.first_name} {c.last_name}
                    </Link>
                    <div className="mt-0.5 text-xs font-semibold text-slate-500 truncate flex items-center gap-1.5">
                        <Briefcase className="h-3 w-3 shrink-0" />
                        {c.layan_offer_title || c.position || "—"}
                    </div>
                </div>
                {c.ai_score > 0 && (
                    <div className="text-[11px] font-extrabold px-2 py-0.5 rounded-full bg-shariti-cream text-slate-600 border border-shariti-gray shrink-0">
                        IA {c.ai_score}
                    </div>
                )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border ${meta.color}`}>
                    <Icon className="h-2.5 w-2.5" />
                    {meta.label}
                </span>
                {c.immersion_scheduled_at && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-600">
                        <Calendar className="h-3 w-3" />
                        {formatDate(c.immersion_scheduled_at)}
                    </span>
                )}
                {c.immersion_location && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 truncate max-w-[160px]" title={c.immersion_location}>
                        <MapPin className="h-3 w-3 shrink-0" />
                        {c.immersion_location}
                    </span>
                )}
            </div>
        </motion.div>
    );
}


export default function MesImmersions() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [list, setList] = useState([]);
    const [search, setSearch] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Backend already filters by pre_recruteur_id for pre-recruteurs
                // via role_scope_query, so this is effectively "my candidates".
                const fetches = await Promise.all(
                    FIELD_STATUSES.map((s) =>
                        api.get("/candidates", { params: { status: s, sort: "recent" } }).then((r) => r.data || []),
                    ),
                );
                if (cancelled) return;
                const merged = fetches.flat();
                // Dedup by id (shouldn't happen but safe)
                const byId = new Map();
                merged.forEach((c) => byId.set(c.id, c));
                // For admins, drop candidates that aren't owned by the current
                // user to keep this page "Mes immersions" specifically.
                const myId = user?.id;
                const filtered = myId
                    ? Array.from(byId.values()).filter((c) =>
                        c.pre_recruteur_id === myId || c.referent_id === myId)
                    : Array.from(byId.values());
                setList(filtered);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [user?.id]);

    const searched = useMemo(() => {
        if (!search) return list;
        const q = search.toLowerCase();
        return list.filter((c) =>
            `${c.first_name || ""} ${c.last_name || ""} ${c.layan_offer_title || ""} ${c.immersion_location || ""}`
                .toLowerCase()
                .includes(q),
        );
    }, [list, search]);

    const buckets = useMemo(() => {
        const upcoming = [];
        const toEvaluate = [];
        const hired = [];
        const now = Date.now();
        searched.forEach((c) => {
            if (c.status === "immersion_scheduled") {
                // If the immersion date is in the past, the immersion has
                // happened (or was a no-show) and is waiting for evaluation
                // by the référent — surface it under "À évaluer" so the
                // recruiter sees they have to chase the scorecard.
                const sched = c.immersion_scheduled_at ? Date.parse(c.immersion_scheduled_at) : null;
                if (sched && sched < now) {
                    toEvaluate.push(c);
                } else {
                    upcoming.push(c);
                }
            } else if (c.status === "immersion_done" || c.status === "awaiting_validation") {
                toEvaluate.push(c);
            } else {
                hired.push(c); // validated / onboarding / hired
            }
        });
        // Sort upcoming by immersion date asc (next first); to-evaluate by date desc (most recently overdue first)
        upcoming.sort((a, b) => (a.immersion_scheduled_at || "").localeCompare(b.immersion_scheduled_at || ""));
        toEvaluate.sort((a, b) => (b.immersion_scheduled_at || "").localeCompare(a.immersion_scheduled_at || ""));
        return { upcoming, toEvaluate, hired };
    }, [searched]);

    if (loading) {
        return (
            <div className="flex justify-center py-20" data-testid="mes-immersions-loading">
                <Loader2 className="h-8 w-8 animate-spin text-shariti-orange" />
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-24 md:pb-8" data-testid="mes-immersions-page">
            {/* Header */}
            <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-shariti-orange-soft text-shariti-orange border border-shariti-orange/20 text-[10px] font-bold uppercase tracking-widest">
                    <Trophy className="h-3 w-3" /> Suivi terrain
                </div>
                <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                    Mes immersions
                </h1>
                <p className="mt-1 text-sm font-medium text-slate-500">
                    Tous les candidats que vous avez envoyés en immersion · {list.length} dossier{list.length > 1 ? "s" : ""} suivi{list.length > 1 ? "s" : ""}
                </p>
            </div>

            {/* Search */}
            {list.length > 0 && (
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher un candidat, une offre…"
                        data-testid="mes-immersions-search"
                        className="w-full pl-9 pr-3 h-10 rounded-full bg-white border border-shariti-gray text-sm focus:outline-none focus:border-shariti-orange transition"
                    />
                </div>
            )}

            {list.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl border border-shariti-gray" data-testid="mes-immersions-empty">
                    <Trophy className="h-10 w-10 mx-auto text-slate-300" />
                    <p className="mt-4 font-bold text-shariti-black">Aucune immersion programmée pour le moment</p>
                    <p className="mt-1 text-sm text-slate-500">
                        Quand vous programmerez un entretien immersif depuis la fiche d'un candidat, il apparaîtra ici.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Bucket 1 — À venir */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-shariti-orange" />
                            <h2 className="text-[11px] font-extrabold uppercase tracking-widest text-shariti-orange">
                                À venir ({buckets.upcoming.length})
                            </h2>
                        </div>
                        {buckets.upcoming.length === 0 ? (
                            <p className="text-xs italic text-slate-400">Aucune immersion à venir</p>
                        ) : (
                            buckets.upcoming.map((c) => <CandidateRow key={c.id} c={c} />)
                        )}
                    </div>

                    {/* Bucket 2 — À évaluer */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-amber-500" />
                            <h2 className="text-[11px] font-extrabold uppercase tracking-widest text-amber-700">
                                À évaluer ({buckets.toEvaluate.length})
                            </h2>
                        </div>
                        {buckets.toEvaluate.length === 0 ? (
                            <p className="text-xs italic text-slate-400">Aucune immersion à évaluer</p>
                        ) : (
                            buckets.toEvaluate.map((c) => <CandidateRow key={c.id} c={c} displayBucket="to_evaluate" />)
                        )}
                    </div>

                    {/* Bucket 3 — Embauché·e·s */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-emerald-500" />
                            <h2 className="text-[11px] font-extrabold uppercase tracking-widest text-emerald-700">
                                Embauché·e·s ({buckets.hired.length})
                            </h2>
                        </div>
                        {buckets.hired.length === 0 ? (
                            <p className="text-xs italic text-slate-400">Aucun candidat validé pour le moment</p>
                        ) : (
                            buckets.hired.map((c) => <CandidateRow key={c.id} c={c} />)
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
